import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  WhatsappLogo, CheckCircle, XCircle, Copy, Lightning, ChatCircle,
  WarningCircle, Trash, Plus, ClockCounterClockwise, ArrowClockwise, PaperPlaneTilt,
  UsersThree,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function WhatsApp() {
  const [cfg, setCfg] = useState({ connected: false, owner_phone: "", device_token_set: false, webhook_secret_set: false, allowed_phones_count: 0, allowed_groups_count: 0, group_trigger_keyword: "" });
  const [form, setForm] = useState({ device_token: "", owner_phone: "", webhook_secret: "", group_trigger_keyword: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [allowed, setAllowed] = useState([]);
  const [newAllowed, setNewAllowed] = useState({ phone: "", label: "", can_approve: false });
  const [groups, setGroups] = useState([]);
  const [newGroup, setNewGroup] = useState({ group_id: "", name: "", forward_approvals: false });
  const [fonnteGroups, setFonnteGroups] = useState(null); // null = belum pernah fetch
  const [fetchingGroups, setFetchingGroups] = useState(false);
  const [logs, setLogs] = useState([]);
  const [sendForm, setSendForm] = useState({ phone: "", text: "" });

  const loadAll = () => {
    api.get("/whatsapp/config").then((r) => { setCfg(r.data); setForm((f) => ({ ...f, owner_phone: r.data.owner_phone || "", group_trigger_keyword: r.data.group_trigger_keyword || "" })); }).catch(() => {});
    api.get("/whatsapp/allowed-phones").then((r) => setAllowed(r.data.allowed || [])).catch(() => {});
    api.get("/whatsapp/allowed-groups").then((r) => setGroups(r.data.allowed_groups || [])).catch(() => {});
    api.get("/whatsapp/logs?limit=50").then((r) => setLogs(r.data || [])).catch(() => {});
    api.get("/whatsapp/webhook-url").then((r) => setWebhookUrl(r.data.webhook_url || "")).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const saveCfg = async () => {
    if (!form.device_token && !cfg.device_token_set) { toast.error("Isi Device Token Fonnte"); return; }
    if (!form.owner_phone) { toast.error("Isi nomor WA pemilik"); return; }
    setSaving(true);
    try {
      await api.post("/whatsapp/config", form);
      toast.success("Konfigurasi tersimpan");
      setForm((f) => ({ ...f, device_token: "" }));
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/whatsapp/test", {});
      if (r.data?.status) toast.success("Pesan test terkirim — cek WhatsApp Anda");
      else toast.error("Fonnte menolak: " + (r.data?.reason || JSON.stringify(r.data)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
    finally { setTesting(false); }
  };

  const addAllowed = async () => {
    if (!newAllowed.phone) return;
    try {
      await api.post("/whatsapp/allowed-phones", newAllowed);
      setNewAllowed({ phone: "", label: "", can_approve: false });
      loadAll();
      toast.success("Nomor ditambahkan");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const removeAllowed = async (phone) => {
    try { await api.delete(`/whatsapp/allowed-phones/${phone}`); loadAll(); toast.success("Dihapus"); }
    catch (e) { toast.error("Gagal menghapus"); }
  };

  const addGroup = async (preset) => {
    const payload = preset || newGroup;
    if (!payload.group_id) { toast.error("Isi Group ID"); return; }
    try {
      await api.post("/whatsapp/allowed-groups", payload);
      setNewGroup({ group_id: "", name: "", forward_approvals: false });
      loadAll();
      toast.success("Group ditambahkan ke whitelist");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah group"); }
  };

  const removeGroup = async (gid) => {
    try { await api.delete(`/whatsapp/allowed-groups/${encodeURIComponent(gid)}`); loadAll(); toast.success("Group dihapus"); }
    catch (e) { toast.error("Gagal menghapus group"); }
  };

  const fetchFonnteGroups = async () => {
    setFetchingGroups(true);
    try {
      const r = await api.post("/whatsapp/fetch-groups", {});
      setFonnteGroups(r.data.groups || []);
      if (!(r.data.groups || []).length) toast.info("Device belum tergabung di group manapun (atau perlu refresh di Fonnte)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengambil daftar group"); }
    finally { setFetchingGroups(false); }
  };

  const sendMsg = async () => {
    if (!sendForm.phone || !sendForm.text) return;
    try {
      const r = await api.post("/whatsapp/send", sendForm);
      if (r.data?.status) { toast.success("Pesan terkirim"); setSendForm({ phone: "", text: "" }); }
      else toast.error("Gagal: " + (r.data?.reason || "unknown"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const clearLogs = async () => {
    if (!window.confirm("Hapus semua log percakapan?")) return;
    await api.delete("/whatsapp/logs");
    loadAll();
  };

  const copyWebhook = () => { navigator.clipboard.writeText(webhookUrl); toast.success("URL disalin"); };

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <WhatsappLogo size={28} weight="fill" className="text-green-600" />
          WhatsApp Bot
        </h1>
        <p className="text-sm text-slate-500 mt-1">Integrasi 2 arah via Fonnte Gateway (ID). Dukung command, AI natural language, foto struk, dan tombol konfirmasi.</p>
      </div>

      {/* Config */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-semibold flex items-center gap-2"><Lightning size={16} className="text-amber-500" />Konfigurasi</h2>
          {cfg.connected ? (
            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle size={12} weight="fill" />Terhubung</span>
          ) : (
            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1"><XCircle size={12} />Belum terhubung</span>
          )}
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Device Token Fonnte {cfg.device_token_set && <span className="text-emerald-600">(sudah diset)</span>}</Label>
            <Input type="password" placeholder={cfg.device_token_set ? "••••• (biarkan kosong jika tidak ganti)" : "Dari menu Device di dashboard Fonnte"}
                   value={form.device_token} onChange={(e) => setForm({ ...form, device_token: e.target.value })}
                   data-testid="wa-device-token" />
          </div>
          <div>
            <Label className="text-xs">Nomor WA Pemilik (owner)</Label>
            <Input placeholder="628xxxxxxxxxx" value={form.owner_phone}
                   onChange={(e) => setForm({ ...form, owner_phone: e.target.value })}
                   data-testid="wa-owner-phone" />
            <div className="text-[11px] text-slate-400 mt-0.5">Format internasional, tanpa + atau spasi. Contoh: 628123456789</div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Webhook Secret {cfg.webhook_secret_set && <span className="text-emerald-600">(sudah diset)</span>}</Label>
            <Input placeholder="(opsional) string acak untuk verifikasi webhook" value={form.webhook_secret}
                   onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                   data-testid="wa-webhook-secret" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Keyword Trigger di Group</Label>
            <Input placeholder="@bot" value={form.group_trigger_keyword}
                   onChange={(e) => setForm({ ...form, group_trigger_keyword: e.target.value })}
                   data-testid="wa-group-keyword" />
            <div className="text-[11px] text-slate-400 mt-0.5">Di group, bot hanya merespon pesan berawalan <code>/</code> (mis. <code>/saldo</code>) atau keyword ini (mis. <code>@bot berapa piutang?</code>). Default: <code>@bot</code>.</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={saveCfg} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="wa-save-btn">
            {saving ? "Menyimpan..." : "Simpan Konfigurasi"}
          </Button>
          {cfg.connected && (
            <Button variant="outline" onClick={sendTest} disabled={testing} data-testid="wa-test-btn">
              <PaperPlaneTilt size={14} className="mr-1" />{testing ? "Mengirim..." : "Kirim Pesan Test"}
            </Button>
          )}
        </div>
      </section>

      {/* Webhook URL */}
      {webhookUrl && (
        <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900 space-y-2">
          <div className="font-semibold flex items-center gap-2"><WarningCircle size={16} weight="fill" />Langkah terakhir — pasang Webhook di Fonnte</div>
          <ol className="list-decimal pl-5 text-xs space-y-1">
            <li>Login Fonnte → menu <b>Device</b> → klik tombol <b>Edit</b> device Anda.</li>
            <li>Aktifkan <b>Auto Read</b>.</li>
            <li>Isi field <b>URL</b> dengan URL di bawah ini:</li>
          </ol>
          <div className="flex items-center gap-2 bg-white border border-amber-200 rounded px-2 py-1.5">
            <code className="text-xs flex-1 break-all" data-testid="wa-webhook-url">{webhookUrl}</code>
            <Button size="sm" variant="ghost" onClick={copyWebhook}><Copy size={14} /></Button>
          </div>
          <div className="text-[11px]">Setelah Anda isi URL + klik Simpan di Fonnte, semua pesan masuk ke nomor device akan diteruskan ke sistem.</div>
        </section>
      )}

      {/* Whitelist */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><ChatCircle size={16} />Whitelist Nomor Terotorisasi</h2>
        <p className="text-xs text-slate-500">Hanya nomor WA berikut (dan nomor owner) yang bisa menggunakan bot. Nomor di luar whitelist akan ditolak dan owner mendapat notifikasi.</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-xs">Nomor WA (628...)</Label>
            <Input placeholder="628123456789" value={newAllowed.phone}
                   onChange={(e) => setNewAllowed({ ...newAllowed, phone: e.target.value })}
                   data-testid="wa-new-phone" />
          </div>
          <div className="flex-1">
            <Label className="text-xs">Label</Label>
            <Input placeholder="Mis. Admin Toko" value={newAllowed.label}
                   onChange={(e) => setNewAllowed({ ...newAllowed, label: e.target.value })}
                   data-testid="wa-new-label" />
          </div>
          <label className="flex items-center gap-1 text-xs pb-2 cursor-pointer">
            <input type="checkbox" checked={!!newAllowed.can_approve}
                   onChange={(e) => setNewAllowed({ ...newAllowed, can_approve: e.target.checked })}
                   data-testid="wa-new-can-approve" />
            <span>Boleh approve</span>
          </label>
          <Button onClick={addAllowed} data-testid="wa-add-allowed"><Plus size={14} className="mr-1" />Tambah</Button>
        </div>
        {allowed.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Belum ada nomor di whitelist. Nomor owner <code>{cfg.owner_phone || "-"}</code> otomatis terotorisasi.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {allowed.map((a) => (
              <div key={a.phone} className="py-2 flex items-center gap-3 text-sm">
                <div className="flex-1">
                  <div className="font-medium flex items-center gap-2">
                    {a.label || "Kontak"}
                    {a.can_approve && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Approver</span>}
                  </div>
                  <div className="text-xs text-slate-500">{a.phone}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeAllowed(a.phone)} data-testid={`wa-del-${a.phone}`}>
                  <Trash size={14} className="text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Whitelist Group */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3" data-testid="wa-groups-section">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold flex items-center gap-2"><UsersThree size={16} className="text-green-600" />Whitelist Group WA (Komunikasi 2 Arah)</h2>
          <Button size="sm" variant="outline" onClick={fetchFonnteGroups} disabled={fetchingGroups} data-testid="wa-fetch-groups-btn">
            <ArrowClockwise size={14} className={`mr-1 ${fetchingGroups ? "animate-spin" : ""}`} />{fetchingGroups ? "Mengambil…" : "Ambil Daftar Grup dari Fonnte"}
          </Button>
        </div>
        <p className="text-xs text-slate-500">
          Bot aktif di group berikut. Hanya anggota yang nomornya ada di <b>whitelist nomor</b> yang bisa memakai bot di group.
          Di group, bot hanya merespon pesan berawalan <code>/</code> atau keyword trigger (lihat Konfigurasi) — pesan lain diabaikan agar tidak spam.
        </p>
        {fonnteGroups !== null && (
          <div className="border border-green-200 bg-green-50/50 rounded-md p-3 space-y-1" data-testid="wa-fonnte-groups">
            <div className="text-xs font-semibold text-green-800">Group yang diikuti device Anda ({fonnteGroups.length}) — klik untuk menambahkan:</div>
            {fonnteGroups.length === 0 ? (
              <div className="text-xs text-slate-500 italic">Tidak ada group ditemukan. Pastikan device sudah join group, lalu coba lagi.</div>
            ) : fonnteGroups.map((g) => (
              <div key={g.id} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded px-2 py-1.5">
                <div className="flex-1 min-w-0">
                  <span className="font-medium">{g.name || "(tanpa nama)"}</span>
                  <code className="ml-2 text-slate-400">{g.id}</code>
                </div>
                {groups.some((x) => x.group_id === g.id) ? (
                  <span className="text-emerald-600 flex items-center gap-1"><CheckCircle size={12} weight="fill" />Terdaftar</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => addGroup({ group_id: g.id, name: g.name || "Group WA", forward_approvals: false })} data-testid={`wa-pick-group-${g.id}`}>
                    <Plus size={12} className="mr-1" />Tambah
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs">Group ID (manual)</Label>
            <Input placeholder="120363…@g.us atau angkanya saja" value={newGroup.group_id}
                   onChange={(e) => setNewGroup({ ...newGroup, group_id: e.target.value })}
                   data-testid="wa-new-group-id" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Nama Group</Label>
            <Input placeholder="Mis. Tim Finance" value={newGroup.name}
                   onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                   data-testid="wa-new-group-name" />
          </div>
          <label className="flex items-center gap-1 text-xs pb-2 cursor-pointer">
            <input type="checkbox" checked={!!newGroup.forward_approvals}
                   onChange={(e) => setNewGroup({ ...newGroup, forward_approvals: e.target.checked })}
                   data-testid="wa-new-group-approval" />
            <span>Teruskan notifikasi approval</span>
          </label>
          <Button onClick={() => addGroup()} data-testid="wa-add-group-btn"><Plus size={14} className="mr-1" />Tambah</Button>
        </div>
        {groups.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Belum ada group di whitelist. Bot mengabaikan semua pesan dari group.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {groups.map((g) => (
              <div key={g.group_id} className="py-2 flex items-center gap-3 text-sm" data-testid={`wa-group-row-${g.group_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    {g.name || "Group WA"}
                    {g.forward_approvals && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">Approval</span>}
                  </div>
                  <div className="text-xs text-slate-500">{g.group_id}</div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => removeGroup(g.group_id)} data-testid={`wa-del-group-${g.group_id}`}>
                  <Trash size={14} className="text-red-500" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Send manual */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <h2 className="font-semibold flex items-center gap-2"><PaperPlaneTilt size={16} />Kirim Pesan Manual</h2>
        <div className="grid md:grid-cols-3 gap-2">
          <Input placeholder="Nomor (628…) atau Group ID (…@g.us)" value={sendForm.phone}
                 onChange={(e) => setSendForm({ ...sendForm, phone: e.target.value })} data-testid="wa-send-phone" />
          <Input className="md:col-span-2" placeholder="Isi pesan..." value={sendForm.text}
                 onChange={(e) => setSendForm({ ...sendForm, text: e.target.value })} data-testid="wa-send-text" />
        </div>
        <Button onClick={sendMsg} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="wa-send-btn">Kirim</Button>
      </section>

      {/* Logs */}
      <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2"><ClockCounterClockwise size={16} />Audit Log Percakapan</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={loadAll}><ArrowClockwise size={14} /></Button>
            {logs.length > 0 && <Button size="sm" variant="ghost" onClick={clearLogs}><Trash size={14} className="text-red-500" /></Button>}
          </div>
        </div>
        {logs.length === 0 ? (
          <div className="text-sm text-slate-400 italic">Belum ada percakapan.</div>
        ) : (
          <div className="divide-y divide-slate-100 text-xs max-h-96 overflow-auto">
            {logs.map((l) => (
              <div key={l.id} className="py-2 flex items-start gap-3">
                <div className="w-32 shrink-0 text-slate-400">{new Date(l.created_at).toLocaleString("id-ID")}</div>
                <div className="w-20 shrink-0">
                  <span className={`px-1.5 py-0.5 rounded ${l.direction === "in" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"}`}>
                    {l.direction === "in" ? "⬇ masuk" : "⬆ keluar"}
                  </span>
                </div>
                <div className="w-20 shrink-0 text-slate-500">{l.kind}</div>
                <div className="w-28 shrink-0 text-slate-500">{l.phone}</div>
                <div className="flex-1 text-slate-700 whitespace-pre-wrap">{l.text}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Help */}
      <section className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-xs text-slate-600 space-y-2">
        <h3 className="font-semibold text-slate-800">Perintah yang didukung:</h3>
        <ul className="list-disc pl-5 space-y-0.5">
          <li><code>/laporan</code> — ringkasan Laba Rugi hari ini</li>
          <li><code>/saldo</code> — saldo kas &amp; bank</li>
          <li><code>/piutang</code> &amp; <code>/hutang</code> — total outstanding</li>
          <li><code>/tagihan</code> — 5 faktur terbaru</li>
          <li><code>/beban 50000 Kategori</code> — catat beban cepat</li>
          <li><code>/pendapatan 200000 Deskripsi</code> — catat pendapatan</li>
          <li><code>/faktur Budi 500000 Jasa desain</code> — buat faktur (perlu konfirmasi)</li>
          <li><b>Kirim foto struk</b> → AI Vision ekstrak jadi expense</li>
          <li><b>Kirim Voice Note 🎙 (bebas bahasa Indonesia)</b> seperti "beli bensin 200 ribu bayar pakai BCA" — Whisper transkrip &amp; AI siapkan transaksi</li>
          <li><b>Natural language</b> seperti "Tagih PT XYZ Rp 3jt konsultasi" → AI siapkan dokumen</li>
          <li><b>Pusat Persetujuan 📋</b> — ajukan dokumen (SO/PO/Fund Request) di aplikasi, approver dgn flag <b>"Boleh approve"</b> di whitelist akan menerima notifikasi WA berisi tombol <b>✅ Setujui</b> / <b>❌ Tolak</b> — langsung approve tanpa buka aplikasi</li>
          <li><b>Group WA 2 arah 👥</b> — tambahkan group ke whitelist; di group bot hanya merespon awalan <code>/</code> atau keyword trigger (default <code>@bot</code>), mis. <code>@bot berapa piutang?</code>. Hanya anggota yang nomornya ter-whitelist yang bisa memakai bot di group</li>
          <li><b>Persetujuan via Group 📣</b> — group dengan opsi "Teruskan notifikasi approval" ikut menerima ringkasan dokumen; approver terotorisasi cukup balas <b>setuju</b> / <b>tolak</b> di group</li>
        </ul>
        <p>Setiap transaksi otomatis minta konfirmasi via tombol ✅/❌ atau balas ketik <b>ya</b>/<b>batal</b>.</p>
      </section>
    </div>
  );
}
